import { isAppActive, subscribeAppActivity } from './appActivity';

// Opt-in reads only: a timed-out mutation may already have reached the server.
export const LOAD_RECOVERY_MS = 20000;
const ATTEMPT_TIMEOUT_MS = 8000;
const TRANSIENT_STATUS = new Set([0, 408, 425, 429, 500, 502, 503, 504, 600]);
const RETRY_DELAYS = [500, 1500, 3000, 5000];
const cancelledResult = () => ({ success: false, cancelled: true });

function pause(delay, signal) {
  return new Promise(resolve => {
    const finish = () => {
      clearTimeout(timer);
      signal.removeEventListener('abort', finish);
      resolve();
    };
    const timer = setTimeout(finish, delay);
    signal.addEventListener('abort', finish, { once: true });
    if (signal.aborted) finish();
  });
}

// The deadline covers foreground work, not time spent suspended by iOS. Each
// resume replaces only this pending read's transport. A transport that ignores
// abort cannot hold the new attempt hostage or deliver an obsolete response.
export function loadWithRecovery(load, { signal } = {}) {
  if (signal?.aborted) return Promise.resolve(cancelledResult());
  return new Promise((resolve, reject) => {
    let controller, deadline, unsubscribe;
    let done = false;
    let lastFailure;
    let retryAt = 0;
    let started = 0;
    const stopCycle = () => { clearTimeout(deadline); controller?.abort(); };
    const finish = (result, error) => {
      if (done) return;
      done = true;
      unsubscribe?.();
      signal?.removeEventListener('abort', abort);
      stopCycle();
      if (error) reject(error); else resolve(result);
    };
    const abort = () => finish(cancelledResult());
    const start = () => {
      stopCycle();
      if (done || !isAppActive()) return;
      const cycle = new AbortController();
      controller = cycle;
      started = Date.now();
      const current = () => !done && !cycle.signal.aborted;
      deadline = setTimeout(() => {
        if (current()) finish(lastFailure || { success: false, statusCode: 408 });
      }, LOAD_RECOVERY_MS);
      const run = async () => {
        // A foreground/online event must not bypass a server's Retry-After.
        if (retryAt > Date.now()) await pause(retryAt - Date.now(), cycle.signal);
        for (let attempt = 0; current(); attempt += 1) {
          const response = await load({ signal: cycle.signal,
            timeout: Math.min(ATTEMPT_TIMEOUT_MS, Math.max(1, LOAD_RECOVERY_MS - (Date.now() - started))) });
          if (!current()) return;
          if (response?.success || response?.cancelled || !TRANSIENT_STATUS.has(response?.statusCode)) {
            finish(response);
            return;
          }
          lastFailure = response;
          const retryAfter = Number(response.retryAfter);
          retryAt = Date.now() + (Number.isFinite(retryAfter) && retryAfter > 0 ? retryAfter * 1000 : 0);
          await pause(Math.max(RETRY_DELAYS[Math.min(attempt, RETRY_DELAYS.length - 1)], retryAt - Date.now()), cycle.signal);
        }
      };
      run().catch(error => { if (current()) finish(undefined, error); });
    };
    unsubscribe = subscribeAppActivity(event => {
      if (!event.active) stopCycle();
      // Native + browser wake-up signals can arrive together. Do not abort a
      // fresh resumed connection again just because the network also woke up.
      else if (event.reason !== 'online' || Date.now() - started >= 1000) start();
    });
    signal?.addEventListener('abort', abort, { once: true });
    start();
  });
}

// Adapter for fetch/Axios readers which throw instead of returning an envelope.
// Keep their public return type, error codes and authentication behavior.
export async function readWithRecovery(read, options) {
  const result = await loadWithRecovery(async request => {
    try { return { success: true, value: await read(request) }; }
    catch (error) {
      const status = error.status ?? error.response?.status;
      if (status === undefined && !(error instanceof TypeError) && error.name !== 'AbortError'
        && error.code !== 'ECONNABORTED' && error.code !== 'ERR_NETWORK') throw error;
      return { success: false, statusCode: status ?? 600, retryAfter: error.retryAfter, error };
    }
  }, options);
  if (result.success) return result.value;
  if (result.cancelled) throw new DOMException('Read cancelled', 'AbortError');
  if (result.error) throw result.error;
  throw Object.assign(new Error('LOAD_TIMEOUT'), { status: 408 });
}
